/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createContext, useContext, useMemo } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import type { PropertyRequiredFields } from '../../hooks/usePropertySettings';
import { fieldsRequiredFor } from '../PropertyFormDualPane/schema.agency-required';
import type { PropertyFormValues } from './schema';

const NONE: ReadonlySet<string> = new Set();

/**
 * The fields the agency requires of the property as it is being filled in,
 * following the transactions, category and condominium the broker picks.
 *
 * @param control - The drawer form's control.
 * @param required - The agency's lists, or undefined until they load.
 * @returns The required field keys.
 */
export function useAgencyRequiredFields(
  control: Control<PropertyFormValues>,
  required: PropertyRequiredFields | undefined,
): ReadonlySet<string> {
  const [transactionType, category, condominium] = useWatch({
    control,
    name: ['transaction_type', 'category', 'condominium'],
  });
  return useMemo(() => {
    if (!required) return NONE;
    return new Set(fieldsRequiredFor(required, {
      transaction_type: transactionType, category, condominium,
    }));
  }, [required, transactionType, category, condominium]);
}

/**
 * The fields the agency requires of the property being edited, right now:
 * Configurações > Imóveis for its transactions, less what the form hides.
 * The drawer provides it; each field reads it to mark itself required.
 */
export const AgencyRequiredFieldsContext = createContext<ReadonlySet<string>>(NONE);

/**
 * Whether the agency requires this field of the property being edited.
 *
 * @param field - The form field key.
 * @returns True when the field must be filled before saving.
 */
export function useAgencyRequired(field: string): boolean {
  return useContext(AgencyRequiredFieldsContext).has(field);
}
